import { BackLink } from '@/components/BackLink';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { Spinner } from '@/components/Spinner';
import { StatusBadge } from '@/components/StatusBadge';
import { useTranslation } from '@/i18n/useTranslation';
import { useKioskSession } from '@/lib/kioskSession';

/**
 * The lookup's loading state [Figma · 03 · Routing].
 *
 * **This is what the frame named "Routing" actually draws** — a spinner and *"Checking your record…"* inside
 * the same card. An earlier build read the name and invented a set of outcome cards; the design routes the
 * candidate onward to screens owned by other stories instead (see `RoutingScreen`).
 *
 * It DOES draw a Back control, and this said the opposite until 2026-09-02: the frame's header is 48 tall
 * because it holds a 69x36 back button, and rendering only the badge made it 33. A lookup against a slow
 * Bullhorn is the longest unexplained wait in the flow, so it is also the screen where a way out matters
 * most. Leaving is safe: `submitLookup` drops any answer that is not for the attempt still being waited on,
 * including one that lands after the candidate has corrected a factor and pressed Continue again.
 *
 * `aria-busy` with a polite live region, so the wait is announced once rather than being a silent pause for
 * anyone not watching the spinner.
 */
export function CheckingScreen() {
  const t = useTranslation();
  const returnToCheckIn = useKioskSession((state) => state.returnToCheckIn);

  /*
   * No Exit here either: `03 · Routing` sets its `Exit button` instance `visible: false` [`12438:27524`],
   * the only flow frame besides Welcome that does. The Back in the card's header is already the way out of
   * a wait, and it is the cheaper one — it keeps the four factors so the candidate can retry.
   */
  return (
    <KioskScreen showExit={false}>
      <KioskCard
        header={
          <>
            <BackLink onClick={returnToCheckIn} />
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
      >
        <div
          aria-busy="true"
          aria-live="polite"
          className="flex w-full flex-col items-center gap-4 py-12"
        >
          <Spinner />
          <p className="text-sm leading-6 text-[var(--muted-foreground)]">
            {t('routing.checking')}
          </p>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
